/**
 * The web console's body links for a typed page's Links section, read from
 * the team's page links index over the project's TeamRoom socket. Data only:
 * it returns the same rows the desktop's Links section reads from its local
 * index (`trackerPageLinks.ts`, rows `body:<rel>` / `body:link`), so the
 * shared section groups them the same way. Field relations come from the
 * tracker store and are the caller's to add.
 *
 * Like the desktop, only typed pages are listed, in both directions: links
 * from plain pages are in the index (`PageLinkEntry.source.kind === 'page'`)
 * but the desktop's section does not show them. A ref resolves through the
 * project's live items, so a deleted item, or one this member cannot see,
 * never shows.
 */

import type { PageLinkEntry, PageLinkTarget, TeamPageLinksQueryMessage, TeamPageLinksResponseMessage } from '@nimbalyst/collab-protocol';

/** A live item of the project, as the Links section needs it. */
export interface WebPageLinkItem {
  id: string;
  issueKey: string | null;
  typeId: string;
  title: string;
}

/** The desktop's `TrackerPageLink` row for a body link. */
export interface WebTrackerPageLink {
  direction: 'out' | 'in';
  predicateId: string | null;
  relationshipTypeKey: string | null;
  otherItemId: string;
  otherTitle: string;
  otherIssueKey: string | null;
  otherTypeId: string;
  sentence: string | null;
  sourceFieldId: string;
}

export interface WebPageLinksResult {
  links: WebTrackerPageLink[];
  /** `partial` until the server has indexed every page once. */
  status: TeamPageLinksResponseMessage['status'];
}

export interface WebPageLinksSourceOptions {
  /** The console's team project. */
  projectId: string;
  /** Null when the TeamRoom is not connected or did not answer. */
  query(filters: Pick<TeamPageLinksQueryMessage, 'projectId' | 'from' | 'to'>): Promise<Pick<TeamPageLinksResponseMessage, 'outgoing' | 'incoming' | 'status'> | null>;
  /** A live item of the project by id or issue key; null for anything else. */
  resolveItem(ref: string): WebPageLinkItem | null;
  /** The server's "links changed" signal, when the host has one. */
  onChanged?(listener: () => void): () => void;
}

export interface WebPageLinksSource {
  /** Body links of a typed page; null while the answer is unavailable (offline, no reply). */
  linksFor(itemId: string): Promise<WebPageLinksResult | null>;
  subscribe(listener: () => void): () => void;
}

function row(direction: 'out' | 'in', entry: PageLinkEntry, other: WebPageLinkItem): WebTrackerPageLink {
  return {
    direction,
    predicateId: entry.rel,
    relationshipTypeKey: entry.rel ?? 'link',
    otherItemId: other.id,
    otherTitle: other.title,
    otherIssueKey: other.issueKey,
    otherTypeId: other.typeId,
    sentence: entry.sentence,
    sourceFieldId: `body:${entry.rel ?? 'link'}`,
  };
}

export function createWebPageLinksSource(options: WebPageLinksSourceOptions): WebPageLinksSource {
  return {
    async linksFor(itemId) {
      const self = options.resolveItem(itemId);
      if (!self) return { links: [], status: 'ready' };
      const to: PageLinkTarget[] = [{ kind: 'item', ref: self.id }, ...(self.issueKey ? [{ kind: 'item' as const, ref: self.issueKey }] : [])];
      const result = await options.query({ projectId: options.projectId, from: { kind: 'item', itemId: self.id }, to });
      if (!result) return null;

      // One row per direction, relation and other item, as the desktop index keeps them.
      const rows = new Map<string, WebTrackerPageLink>();
      const add = (direction: 'out' | 'in', entry: PageLinkEntry, otherRef: string) => {
        const other = options.resolveItem(otherRef);
        if (!other || other.id === self.id) return;
        const link = row(direction, entry, other);
        const key = `${direction}|${link.sourceFieldId}|${other.id}`;
        if (!rows.has(key)) rows.set(key, link);
      };
      for (const entry of result.outgoing) {
        if (entry.target.kind === 'item') add('out', entry, entry.target.ref);
      }
      for (const entry of result.incoming) {
        if (entry.source.kind === 'item') add('in', entry, entry.source.itemId);
      }
      return { links: [...rows.values()], status: result.status };
    },
    subscribe(listener) {
      return options.onChanged?.(listener) ?? (() => undefined);
    },
  };
}
